import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { api, getToken, onUnauthorized, setToken, tokenExpiresAt } from '../api/client';
import type { Me } from '../api/types';

interface AuthState {
  me: Me | null;
  loading: boolean;
  /** 세션 만료로 자동 로그아웃되었는지 — 로그인 화면에서 안내 표시 */
  sessionExpired: boolean;
  clearSessionExpired: () => void;
  login: (loginId: string, pw: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
  hasRole: (...roles: string[]) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

/** setTimeout 최대 지연(약 24.8일) — 초과 값은 즉시 발화하므로 잘라 쓴다 */
const MAX_TIMEOUT = 2 ** 31 - 1;

/**
 * 인증 컨텍스트.
 *
 * 세션 만료 처리(액세스 토큰 TTL 30분):
 *  1) **선제 로그아웃** — 토큰의 `exp` 시각에 타이머를 걸어, 요청이 없어도 만료 즉시 로그아웃한다.
 *     (만료된 토큰으로 화면이 열린 채 API 만 실패하는 상태를 없앤다)
 *  2) **사후 로그아웃** — 인증 요청이 401 을 받으면 즉시 로그아웃한다(서버 재기동·강제 무효화 대비).
 *  두 경로 모두 `sessionExpired` 를 세워 로그인 화면에 사유를 안내한다.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  /** 명시적 로그아웃 — expired=true 면 만료 안내를 남긴다 */
  const doLogout = useCallback(
    (expired: boolean) => {
      clearTimer();
      setToken(null);
      setMe(null);
      if (expired) setSessionExpired(true);
    },
    [clearTimer],
  );

  /** 토큰 만료 시각에 자동 로그아웃 예약 */
  const scheduleExpiry = useCallback(() => {
    clearTimer();
    const exp = tokenExpiresAt();
    if (!exp) return;
    const delay = exp - Date.now();
    if (delay <= 0) {
      doLogout(true);
      return;
    }
    timer.current = setTimeout(() => doLogout(true), Math.min(delay, MAX_TIMEOUT));
  }, [clearTimer, doLogout]);

  const refresh = useCallback(async (): Promise<void> => {
    if (!getToken()) {
      setMe(null);
      setLoading(false);
      return;
    }
    try {
      setMe(await api.get<Me>('/users/me'));
      scheduleExpiry();
    } catch {
      // 401 은 onUnauthorized 가 처리(만료 안내). 그 외 오류도 세션을 신뢰할 수 없으므로 정리한다.
      setToken(null);
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, [scheduleExpiry]);

  // 401(인증 요청 거부) → 즉시 로그아웃 + 만료 안내
  useEffect(() => onUnauthorized(() => doLogout(true)), [doLogout]);

  useEffect(() => {
    void refresh();
    return clearTimer;
  }, []); // 마운트 1회 — 이후 갱신은 login/refresh 가 처리

  // 탭 복귀 시 만료 재확인 — 절전/백그라운드로 타이머가 늦게 깨어난 경우 대비
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !getToken()) return;
      const exp = tokenExpiresAt();
      if (exp && exp <= Date.now()) doLogout(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [doLogout]);

  const login = async (loginId: string, pw: string): Promise<void> => {
    const { accessToken } = await api.post<{ accessToken: string; refreshToken: string }>('/auth/login', {
      loginId,
      pw,
    });
    setToken(accessToken);
    setSessionExpired(false);
    await refresh();
  };

  const value = useMemo<AuthState>(
    () => ({
      me,
      loading,
      sessionExpired,
      clearSessionExpired: () => setSessionExpired(false),
      login,
      logout: () => doLogout(false),
      refresh,
      hasRole: (...roles: string[]) => !!me && roles.some((r) => me.roles.includes(r)),
    }),
    [me, loading, sessionExpired, refresh, doLogout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
